import React, { useEffect, useState } from "react";
import { MoreHorizontal, Download, Link2, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useBackHandler } from "@/lib/backButton";
import { consumeEscape } from "@/lib/escapeLayer";
import { saveMediaToGallery, shareMediaFile } from "@/lib/saveMedia";
import { toShareLink } from "@/lib/share";

const ITEM = "w-full flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl text-left text-[15px] md:text-sm font-medium text-white hover:bg-white/10 active:bg-white/15 transition-colors disabled:opacity-60";

// Toasts communs aux téléchargements (visionneuse + menu ⋮ du fil). `n` > 1 : album entier.
export const toastSaveResult = (t, how, n = 1) => {
  const nn = (s) => s.replace("{n}", String(n));
  if (how === "gallery") toast.success(n > 1 ? nn(t("viewer.saved_gallery_n", "{n} médias enregistrés dans ta galerie")) : t("viewer.saved_gallery", "Enregistré dans ta galerie"));
  else if (how === "downloaded") toast.success(n > 1 ? nn(t("viewer.download_started_n", "{n} téléchargements lancés")) : t("viewer.download_started", "Téléchargement lancé"));
  else if (how === "opened") toast(t("viewer.opened_tab", "Média ouvert dans un nouvel onglet : clic droit (ou appui long) › Enregistrer."));
  else if (how === "denied") toast.error(t("viewer.gallery_denied", "Autorise l'accès aux photos dans les réglages du téléphone pour enregistrer."));
  else if (how === "error") toast.error(t("viewer.download_failed", "Téléchargement impossible. Réessaie."));
};

// Menu « ⋯ » de la visionneuse plein écran (façon Facebook) : téléphone → feuille en bas ; PC → menu ancré sous le bouton.
// `downloadUrl` absent (texte sur fond, vidéo par lien) → seul « Copier le lien » reste. `shareLink` = URL interne
// (/post/:id, /profile/:id, /churches/:id) convertie en lien public riche.
export const LightboxMenu = ({ shareLink = null, downloadUrl = null, downloadKind = "image", style }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const close = () => setOpen(false);

  // Échap ferme le menu seul (capture → la visionneuse ignore ce même appui) ; bouton Retour Android idem (pile LIFO).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { consumeEscape(e); e.stopPropagation(); close(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);
  useBackHandler(open, () => { close(); return true; });

  if (!shareLink && !downloadUrl) return null;
  const publicLink = shareLink ? toShareLink(shareLink) : "";
  const isVideo = downloadKind === "video";

  const download = async () => {
    if (busy) return;
    setBusy("download");
    try {
      const how = await saveMediaToGallery({ url: downloadUrl, kind: downloadKind });
      toastSaveResult(t, how);
      if (how !== "cancelled") close();
    } catch { toastSaveResult(t, "error"); } finally { setBusy(null); }
  };
  const share = async () => {
    if (busy) return;
    setBusy("share");
    try {
      const how = await shareMediaFile({ url: downloadUrl, kind: downloadKind, link: publicLink });
      if (how !== "cancelled") close();
    } catch { toast.error(t("viewer.share_failed", "Partage impossible. Réessaie.")); } finally { setBusy(null); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success(t("common.link_copied", "Lien copié"));
    } catch { toast.error(t("poster.copy_fail", "Copie impossible")); }
    close();
  };
  const Icon = ({ name, children }) => (
    <span className="grid place-items-center h-9 w-9 rounded-full bg-white/10 shrink-0">{busy === name ? <Loader2 size={18} className="animate-spin" /> : children}</span>
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="absolute z-10 grid place-items-center h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        style={style}
        aria-label={t("common.more", "Plus")}
        aria-expanded={open}
        data-testid="lightbox-menu-btn"
      >
        <MoreHorizontal size={22} />
      </button>
      {open && (
        <div className="absolute inset-0 z-20" onClick={(e) => { e.stopPropagation(); close(); }} data-testid="lightbox-menu-backdrop">
          <div
            className="fixed inset-x-0 bottom-0 rounded-t-2xl bg-[#1c1f24]/95 backdrop-blur-md border-t border-white/10 px-2 pt-2 pb-[calc(var(--sa-bottom,0px)+0.75rem)] shadow-[0_-8px_30px_rgba(0,0,0,0.45)] animate-in slide-in-from-bottom-6 fade-in duration-200 md:absolute md:inset-x-auto md:bottom-auto md:top-[calc(var(--sa-top,0px)+3.75rem)] md:right-[calc(var(--sa-right,0px)+1rem)] md:w-72 md:rounded-2xl md:border md:pb-2 md:slide-in-from-top-2 md:slide-in-from-bottom-0"
            onClick={(e) => e.stopPropagation()}
            role="menu"
            data-testid="lightbox-menu"
          >
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/25 md:hidden" aria-hidden />
            {downloadUrl && (
              <button type="button" onClick={download} disabled={!!busy} className={ITEM} role="menuitem" data-testid="lightbox-menu-download" data-kind={downloadKind}>
                <Icon name="download"><Download size={18} /></Icon>
                {isVideo ? t("viewer.download_video", "Télécharger la vidéo") : t("viewer.download_image", "Télécharger l'image")}
              </button>
            )}
            {downloadUrl && (
              <button type="button" onClick={share} disabled={!!busy} className={ITEM} role="menuitem" data-testid="lightbox-menu-share" data-kind={downloadKind}>
                <Icon name="share"><Share2 size={18} /></Icon>
                {isVideo ? t("viewer.share_video", "Partager la vidéo") : t("viewer.share_image", "Partager l'image")}
              </button>
            )}
            {shareLink && (
              <button type="button" onClick={copyLink} disabled={!!busy} className={ITEM} role="menuitem" data-testid="lightbox-menu-copy-link">
                <Icon name="copy"><Link2 size={18} /></Icon>
                {t("common.copy_link", "Copier le lien")}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
